import React from 'react';
import PropTypes from 'prop-types';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
// components
import ErrorText from '../../../components/ErrorText';
import { ShimmerComponent } from '../../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../../components/Shimmer/BouncingLogo';
import SelectDropdown from '../../../components/SelectDropdown';
import NoData from '../../../components/NoData';
import Table from '../../../components/Table';
// hooks
import { useActualVsBudgetHook } from '../hooks/useActualVsBudgetHook';
// constants
import {
  ACTUAL_VS_BUDGET_BRANCH_TOTAL_MONTHLY_FILE,
  ACTUAL_VS_BUDGET_BRANCH_TOTAL_WEEKLY_FILE,
  ACTUAL_VS_BUDGET_BRANCHES_MONTHLY,
  ACTUAL_VS_BUDGET_BRANCHES_WEEKLY,
  ACTUAL_VS_BUDGET_CHANNEL_MONTHLY_FILE,
  ACTUAL_VS_BUDGET_CHANNEL_WEEKLY_FILE,
  ACTUAL_VS_BUDGET_MONTHLY,
  ACTUAL_VS_BUDGET_WEEKLY,
  STICKY_COLUMNS_COLUMN,
  VIEW_TYPE_MONTHLY,
} from '../constants';
import ReportDownloadComponent from '../../../components/DownloadReport/ReportDownloadComponent';
// services
import { overrideStoreIdFilterForSelectedBranches } from '../services/ActualVsBudgetServices';

// Helper functions to get toggle button background and color
const getToggleBackground = (isActive, isDataLoading) => {
  if (isActive) return 'black';
  if (isDataLoading) return '#e4e4e7';
  return '#fafafa';
};

const getToggleColor = (isActive, isDataLoading) => {
  if (isActive) return 'white';
  if (isDataLoading) return '#a9a9ab';
  return 'black';
};

// Helper function to get toggle button styles
const getToggleButtonStyle = (isActive, isDataLoading) => ({
  background: getToggleBackground(isActive, isDataLoading),
  color: getToggleColor(isActive, isDataLoading),
  borderColor: isDataLoading ? '#a9a9ab' : 'black',
  textTransform: 'capitalize',
  height: '40px',
});

// Centered Box style for NoData components
const centeredBoxStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '400px',
};

// Helper component for NoData display
const NoDataBox = ({ textValue }) => (
  <Box sx={centeredBoxStyle}>
    <NoData textValue={textValue} />
  </Box>
);

NoDataBox.propTypes = {
  textValue: PropTypes.string.isRequired,
};

// Helper function to get cell props for table
const createCellPropsGetter = (columnData) => (cellInfo) => {
  if ([`${columnData?.[2]?.accessor}`]?.includes(cellInfo.column.id)) {
    return { style: { backgroundColor: '#FEFBC7' } };
  }
  return { style: {} };
};

// Component for Branch Table content
const BranchTableContent = ({ branchTableData, selectedBranches, error }) => {
  if (branchTableData?.rowData?.length > 0 && selectedBranches?.length) {
    return (
      <Table
        boldHeaders={true}
        boldDataColumns={['branch', 'ytd_spend_to_plan', '_YTD']}
        stickyColumns={STICKY_COLUMNS_COLUMN}
        data={selectedBranches?.length ? branchTableData?.filteredData : branchTableData?.rowData}
        columns={branchTableData?.columnData}
        getCellProps={createCellPropsGetter(branchTableData?.columnData)}
      />
    );
  }

  if (branchTableData?.rowData?.length === 0) {
    return <NoDataBox textValue="No data available" />;
  }

  if (branchTableData?.rowData?.length > 0 && !selectedBranches?.length) {
    return <NoDataBox textValue="No branches selected" />;
  }

  if (error?.branchTableError) {
    return <NoDataBox textValue="Error loading data" />;
  }

  return null;
};

BranchTableContent.propTypes = {
  branchTableData: PropTypes.shape({
    rowData: PropTypes.array,
    filteredData: PropTypes.array,
    columnData: PropTypes.array,
  }),
  selectedBranches: PropTypes.array,
  error: PropTypes.shape({
    branchTableError: PropTypes.bool,
  }),
};

// Component for Channel Table content
const ChannelTableContent = ({ tableData, filteredData, selectedChannels, error }) => {
  if (tableData?.rowData?.length > 1 && selectedChannels?.length > 0) {
    return (
      <Table
        boldHeaders={true}
        boldDataColumns={['channel']}
        stickyColumns={STICKY_COLUMNS_COLUMN}
        data={filteredData?.length > 0 ? filteredData : tableData?.rowData}
        columns={tableData?.columnData}
        getCellProps={createCellPropsGetter(tableData?.columnData)}
      />
    );
  }

  if (tableData?.rowData?.length === 1 && !error?.channelTableError) {
    return <NoDataBox textValue="No data available" />;
  }

  if (tableData?.rowData?.length > 0 && !selectedChannels?.length) {
    return <NoDataBox textValue="No channels selected" />;
  }

  if (tableData?.rowData?.length === 0 && selectedChannels?.length > 0) {
    return <NoDataBox textValue="No data available for selected channel" />;
  }

  if (error?.channelTableError) {
    return <NoDataBox textValue="Error loading data" />;
  }

  return null;
};

ChannelTableContent.propTypes = {
  tableData: PropTypes.shape({
    rowData: PropTypes.array,
    columnData: PropTypes.array,
  }),
  filteredData: PropTypes.array,
  selectedChannels: PropTypes.array,
  error: PropTypes.shape({
    channelTableError: PropTypes.bool,
  }),
};

// Common styles
const mainContainerStyle = {
  height: '88vh',
  overflow: 'scroll',
  padding: '70px',
  paddingBottom: '100px',
  paddingTop: '24px',
};

const headerStyle = {
  width: '100%',
  textAlign: 'left',
  margin: 'auto',
  fontSize: '1.5rem',
  fontWeight: '500',
  lineHeight: '1.6',
};

const flexEndContainerStyle = {
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '12px',
  width: '100%',
};

const tableContainerStyle = {
  width: '100%',
  margin: 'auto',
  padding: '0px 0px',
};

const filterRowStyle = {
  marginRight: 'auto',
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
};

const loadingShimmerStyle = {
  width: '100%',
  height: '100%',
  backgroundSize: '2000px 800px',
  border: '0px',
};

// Helper component for ViewType Toggle
const ViewTypeToggle = ({ initialLoading, viewType, handleViewChange, isDataLoading }) => {
  if (initialLoading?.current) {
    return <ShimmerComponent updatedStyle={{ width: '140px', height: '40px' }} />;
  }

  return (
    <ToggleButtonGroup
      value={viewType}
      exclusive
      onChange={handleViewChange}
      aria-label="Toggle Spends And Opps Table View"
      disabled={isDataLoading}
    >
      <ToggleButton
        value={'Weekly'}
        style={getToggleButtonStyle(viewType === 'Weekly', isDataLoading)}
      >
        Weekly
      </ToggleButton>
      <ToggleButton
        value="Monthly"
        style={getToggleButtonStyle(viewType === VIEW_TYPE_MONTHLY, isDataLoading)}
      >
        Monthly
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

ViewTypeToggle.propTypes = {
  initialLoading: PropTypes.shape({ current: PropTypes.bool }),
  viewType: PropTypes.string,
  handleViewChange: PropTypes.func,
  isDataLoading: PropTypes.bool,
};

// Helper component for loading shimmer with bouncing logo
const LoadingShimmer = ({ text }) => (
  <ShimmerComponent updatedStyle={loadingShimmerStyle}>
    <BouncingLogo height="72vh" text={text} />
  </ShimmerComponent>
);

LoadingShimmer.propTypes = {
  text: PropTypes.string,
};

// Helper component for Branch Download Button
const BranchDownloadSection = ({ config, state, handlers }) => {
  const { initialLoading, viewType, options } = config;
  const {
    actualVsBudgetFiltersBranches,
    selectedBranches,
    isActualVsBudgetBranchesReportLoading,
    isLoading,
    branchTableData,
    error,
  } = state;
  const { setIsActualVsBudgetBranchesReportLoading } = handlers;

  if (initialLoading?.current) {
    return <ShimmerComponent updatedStyle={{ width: '64px' }} />;
  }

  const isDisabled =
    isLoading?.branchTableLoading ||
    branchTableData?.rowData?.length === 0 ||
    (branchTableData?.rowData?.length > 0 && !selectedBranches?.length) ||
    error?.branchTableError ||
    (selectedBranches?.length > 0 && branchTableData?.filteredData?.length === 0);

  return (
    <ReportDownloadComponent
      component={
        viewType === VIEW_TYPE_MONTHLY
          ? ACTUAL_VS_BUDGET_BRANCHES_MONTHLY
          : ACTUAL_VS_BUDGET_BRANCHES_WEEKLY
      }
      appliedFilters={overrideStoreIdFilterForSelectedBranches(
        actualVsBudgetFiltersBranches,
        selectedBranches,
        options
      )}
      filename={
        viewType === VIEW_TYPE_MONTHLY
          ? ACTUAL_VS_BUDGET_BRANCH_TOTAL_MONTHLY_FILE
          : ACTUAL_VS_BUDGET_BRANCH_TOTAL_WEEKLY_FILE
      }
      setIsDownloading={setIsActualVsBudgetBranchesReportLoading}
      isDownloading={isActualVsBudgetBranchesReportLoading}
      disabled={isDisabled}
      customButtonStyle={{ marginTop: '8px' }}
    />
  );
};

BranchDownloadSection.propTypes = {
  config: PropTypes.shape({
    initialLoading: PropTypes.shape({ current: PropTypes.bool }),
    viewType: PropTypes.string,
    options: PropTypes.array,
  }),
  state: PropTypes.shape({
    actualVsBudgetFiltersBranches: PropTypes.object,
    selectedBranches: PropTypes.array,
    isActualVsBudgetBranchesReportLoading: PropTypes.bool,
    isLoading: PropTypes.object,
    branchTableData: PropTypes.object,
    error: PropTypes.object,
  }),
  handlers: PropTypes.shape({
    setIsActualVsBudgetBranchesReportLoading: PropTypes.func,
  }),
};

// Helper component for Channel Download Button
const ChannelDownloadSection = ({ config, state, handlers }) => {
  const { initialLoading, channels, viewType } = config;
  const {
    actualVsBudgetFiltersChannels,
    isActualVsBudgetChannelReportLoading,
    isLoading,
    isTimeoutInProcess,
    tableData,
    selectedChannels,
    error,
  } = state;
  const { setIsActualVsBudgetChannelReportLoading } = handlers;

  if (initialLoading?.current || !channels?.length) {
    return <ShimmerComponent updatedStyle={{ width: '64px' }} />;
  }

  const isDisabled =
    isLoading?.channelTableLoading ||
    isTimeoutInProcess ||
    (tableData?.rowData?.length > 0 && !selectedChannels?.length) ||
    (tableData?.rowData?.length === 0 && selectedChannels?.length) ||
    (tableData?.rowData?.length === 1 && !error?.channelTableError);

  return (
    <ReportDownloadComponent
      component={
        viewType === VIEW_TYPE_MONTHLY ? ACTUAL_VS_BUDGET_MONTHLY : ACTUAL_VS_BUDGET_WEEKLY
      }
      appliedFilters={actualVsBudgetFiltersChannels}
      filename={
        viewType === VIEW_TYPE_MONTHLY
          ? ACTUAL_VS_BUDGET_CHANNEL_MONTHLY_FILE
          : ACTUAL_VS_BUDGET_CHANNEL_WEEKLY_FILE
      }
      setIsDownloading={setIsActualVsBudgetChannelReportLoading}
      isDownloading={isActualVsBudgetChannelReportLoading}
      disabled={isDisabled}
      customButtonStyle={{ marginTop: '8px' }}
      multiComponentPayload
    />
  );
};

ChannelDownloadSection.propTypes = {
  config: PropTypes.shape({
    initialLoading: PropTypes.shape({ current: PropTypes.bool }),
    channels: PropTypes.array,
    viewType: PropTypes.string,
  }),
  state: PropTypes.shape({
    actualVsBudgetFiltersChannels: PropTypes.object,
    isActualVsBudgetChannelReportLoading: PropTypes.bool,
    isLoading: PropTypes.object,
    isTimeoutInProcess: PropTypes.bool,
    tableData: PropTypes.object,
    selectedChannels: PropTypes.array,
    error: PropTypes.object,
  }),
  handlers: PropTypes.shape({
    setIsActualVsBudgetChannelReportLoading: PropTypes.func,
  }),
};

// No stores error view
const NoStoresView = () => (
  <div style={{ height: '88vh', overflow: 'scroll', padding: '24px', paddingBottom: '100px' }}>
    <div style={{ width: 'fitContent', marginBottom: '55px' }}>
      <div
        style={{
          width: '90%',
          textAlign: 'left',
          margin: 'auto',
          fontSize: '1.5rem',
          fontWeight: '500',
          lineHeight: '1.6',
        }}
      >
        <ErrorText reason="No branches assigned to user" />
      </div>
    </div>
  </div>
);

const ActualVsBudget = () => {
  const {
    userStores,
    options,
    isLoading,
    error,
    initialLoading,
    tableData,
    handleStoreIdChange,
    handleResetForStore,
    resetStore,
    viewType,
    handleViewChange,
    filterSelectedChannels,
    filteredData,
    selectedChannels,
    channels,
    filterSelectedBranches,
    selectedBranches,
    branchTableData,
    isTimeoutInProcess,
    setIsActualVsBudgetBranchesReportLoading,
    isActualVsBudgetBranchesReportLoading,
    actualVsBudgetFiltersBranches,
    actualVsBudgetFiltersChannels,
    isActualVsBudgetChannelReportLoading,
    setIsActualVsBudgetChannelReportLoading,
  } = useActualVsBudgetHook();

  const isDataLoading = isLoading?.channelTableLoading || isLoading?.branchTableLoading;
  const isBranchTableLoading = initialLoading?.current || isLoading?.branchTableLoading;
  const isChannelTableLoading =
    initialLoading?.current || isLoading?.channelTableLoading || isTimeoutInProcess;
  const channelLoadingText = isTimeoutInProcess
    ? 'Channel Selection in process'
    : 'Loading data...';

  // Render branch dropdown
  const renderBranchDropdown = () => {
    if (initialLoading?.current) {
      return <ShimmerComponent updatedStyle={{ width: '200px' }} />;
    }
    return (
      <SelectDropdown
        options={options}
        label={'Select Branch'}
        isSelectAll={true}
        allOptionsSelectedText={'All Branches'}
        multiple={true}
        multiSelectDefaultValues={options && [...options.map((branch) => branch?.value)]}
        handleFilterChange={filterSelectedBranches}
        resetValues={resetStore}
        width={'200px'}
        marginExist={false}
        disabled={isLoading?.branchTableLoading}
      />
    );
  };

  // Render channel branch dropdown
  const renderChannelBranchDropdown = () => {
    if (initialLoading?.current) {
      return <ShimmerComponent updatedStyle={{ width: '200px' }} />;
    }
    return (
      <SelectDropdown
        options={options}
        label={'Select Branch'}
        resetValues={resetStore}
        handleResetValues={handleResetForStore}
        singleSelectDefaultValue={options?.[0]?.value}
        width={'200px'}
        handleFilterChange={handleStoreIdChange}
        marginExist={false}
        disabled={isLoading?.channelTableLoading}
      />
    );
  };

  // Render channel dropdown
  const renderChannelDropdown = () => {
    if (initialLoading?.current || !channels?.length) {
      return <ShimmerComponent updatedStyle={{ width: '200px' }} />;
    }
    return (
      <SelectDropdown
        options={channels}
        label={'Select Channel'}
        isSelectAll={true}
        allOptionsSelectedText={'All Channels'}
        multiple={true}
        multiSelectDefaultValues={channels && [...channels.map((channel) => channel?.value)]}
        handleFilterChange={filterSelectedChannels}
        resetValues={resetStore}
        width={'200px'}
        marginExist={false}
        disabled={isLoading?.channelTableLoading}
      />
    );
  };

  if (!userStores?.length) {
    return <NoStoresView />;
  }

  return (
    <div style={mainContainerStyle}>
      <div style={{ width: 'fitContent', marginBottom: '20px' }}>
        <div style={headerStyle}>Actual Vs Budget Spend</div>
      </div>

      <div style={flexEndContainerStyle}>
        <ViewTypeToggle
          initialLoading={initialLoading}
          viewType={viewType}
          handleViewChange={handleViewChange}
          isDataLoading={isDataLoading}
        />
      </div>

      {/* UI for table One - Branch Totals */}
      <div style={tableContainerStyle}>
        <Typography variant="h6" align="left" sx={{ paddingBottom: '8px' }}>
          Actual vs Budget - Branch Totals
        </Typography>
        <div style={filterRowStyle}>
          {renderBranchDropdown()}
          <div style={flexEndContainerStyle}>
            <BranchDownloadSection
              config={{ initialLoading, viewType, options }}
              state={{
                actualVsBudgetFiltersBranches,
                selectedBranches,
                isActualVsBudgetBranchesReportLoading,
                isLoading,
                branchTableData,
                error,
              }}
              handlers={{ setIsActualVsBudgetBranchesReportLoading }}
            />
          </div>
        </div>

        {isBranchTableLoading ? (
          <LoadingShimmer text="Loading..." />
        ) : (
          <BranchTableContent
            branchTableData={branchTableData}
            selectedBranches={selectedBranches}
            error={error}
          />
        )}
      </div>

      {/* UI for table Two - Channel */}
      <div style={tableContainerStyle}>
        <Typography variant="h6" align="left" sx={{ paddingBottom: '8px' }}>
          Actual vs Budget Channel
        </Typography>
        <div style={filterRowStyle}>
          {renderChannelBranchDropdown()}
          {renderChannelDropdown()}
          <div style={flexEndContainerStyle}>
            <ChannelDownloadSection
              config={{ initialLoading, channels, viewType }}
              state={{
                actualVsBudgetFiltersChannels,
                isActualVsBudgetChannelReportLoading,
                isLoading,
                isTimeoutInProcess,
                tableData,
                selectedChannels,
                error,
              }}
              handlers={{ setIsActualVsBudgetChannelReportLoading }}
            />
          </div>
        </div>

        {isChannelTableLoading ? (
          <LoadingShimmer text={channelLoadingText} />
        ) : (
          <ChannelTableContent
            tableData={tableData}
            filteredData={filteredData}
            selectedChannels={selectedChannels}
            error={error}
          />
        )}
      </div>
    </div>
  );
};

export default ActualVsBudget;
